import { Step, StepLabel, Stepper } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useState } from "react";

import { IMissingData, IMissingDataForm } from "../types";
import { IFieldConfiguration } from "../MissingDataForm.types";
import { MissingDataContext } from "./context";
import { CustomConnector } from "./styled";
import { Header } from "../styled";
import { addressOptions, locationOptions } from "./helper";

import { StepConfirmation } from "./StepConfirmation";
import { StepDependents } from "./StepDependents";
import { StepEmployee } from "./StepEmployee";
import { StepSuccess } from "./StepSuccess";


/**
 * - Recebe os dados, porém salva um useState adicionando os campos que nao vem do back! (formData or something)
 *
 * Componente de edicao:
 * - Recebe os dados do componente pai e salva em um useState
 * - isEditing apenas necessario para mudar tela de editar e tals. Quando clica em isEditing, enable os mesmos componentes!
 * - Qando estiver editando os camposm setState para o valor do campo!
 * - Quando cancela isEditing, setState para o estado inicial
 * - Quando salva isEditing, salva o dado no componente pai!
 */

function extractCityName(value: string) {
  if (!value) return "";
  return value.replace(/^\s*\d+\s*-\s*/, "").trim();
}

function extractStateName(value: string): string {
  if (!value) return "";
  // If the value contains " - ", extract the part after it
  if (value.includes(" - ")) {
    const parts = value.split(" - ");
    return parts[1] || "";
  }
  // Otherwise, return as-is (it's probably already just the state name)
  return value.trim();
}

interface IProps {
  missingData: IMissingData;
  config: IFieldConfiguration[];
}

export function MissingDataMultistep({ missingData, config }: IProps) {
  /** Adiciona campos a mais que não vieram do back porém são criados no front. */
  const genderOptions = ["M - Masculino", "F - Feminino"];
  const maritalStatusOptions = ["1 - Solteiro", "2 - Casado", "3 - Divorciado", "4 - Separado", "5 - Viúvo"];
  const dependentTypeOptions = [
    "Cônjuge",
    "Companheiro(a) com o(a) qual tenha filho ou viva há mais de 5 (cinco) anos ou possua declaração de união estável",
    "Filho(a) ou enteado(a)",
    "Irmão(ã), neto(a) ou bisneto(a) sem arrimo dos pais, do(a) qual detenha a guarda judicial",
    "Pais, avós e bisavós",
    "Menor pobre do qual detenha a guarda judicial",
    "A pessoa absolutamente incapaz, da qual seja tutor ou curador",
    "Ex-cônjuge",
    "Agregado/Outros"
  ];

  /** Process state and city data - handle both "CODE - NAME" and "NAME" formats */
  const processedStateName = extractStateName(missingData.state_id);
  const processedCityName = extractCityName(missingData.city_id);

  // Find the state in locationOptions
  const cityState = locationOptions.find(option => option.state === processedStateName);

  // Validate if the processed state name exists in locationOptions
  const stateExists = locationOptions.some(option => option.state === processedStateName);

  // Validate if the processed city name exists in the state's cities
  const cityExists = cityState && cityState.cities.includes(processedCityName);

  const missingDataForm: IMissingDataForm = {
    ...missingData,
    state_id: stateExists ? processedStateName : "",
    city_id: cityExists ? processedCityName : "",
    gender_id: !genderOptions.includes(missingData.gender_id) ? "": missingData.gender_id,
    marital_status_id: !maritalStatusOptions.includes(missingData.marital_status_id) ? "" : missingData.marital_status_id,
    address_type: !addressOptions.map(o => o.value).includes(missingData.address_type) ? "" : missingData.address_type,
    employee_product_type: [],
    dependent_product_type: [],
    dependents: missingData.dependents.map(dependent => ({
      ...dependent,
      marital_status_id: !maritalStatusOptions.includes(dependent.marital_status_id) ? "" : dependent.marital_status_id,
      // Case-insensitive validation for dependent type
      dependent_type_id: !dependentTypeOptions.map(option => option.toLowerCase()).includes(dependent.dependent_type_id.toLowerCase()) ? "" : dependent.dependent_type_id,
      product_type: [],
      // Preserve student value from mapped data (mapped from IS_STUDENT boolean)
      student: dependent.student || "",
      key: String(Math.random()),
    })),
  };

  const [currentStep, setCurrentStep] = useState(0);
  const [data, setData] = useState(missingDataForm);

  function handleNextStep(step?: number) {
    if (step !== undefined) {
      setCurrentStep(step);
      return window.scrollTo({ top: 0, behavior: 'auto' });
    }

    setCurrentStep(prev => prev + 1);
    window.scrollTo({ top: 0, behavior: 'auto' });
  };

  function handlePrevStep(step?: number) {
    if (step !== undefined) {
      setCurrentStep(step);
      return window.scrollTo({ top: 0, behavior: 'auto' });
    }

    setCurrentStep(prev => prev - 1);
    window.scrollTo({ top: 0, behavior: 'auto' });
  };

  function handleUpdateData(payload: IMissingDataForm) {
    setData({ ...payload });
  };

  return (
    <>
      <Header>Confirmação e edição de dados complementares</Header>
      <Stepper
        activeStep={currentStep}
        connector={<CustomConnector />}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          flexDirection: 'row',
          alignItems: 'center',
          px: 2,
        }}
      >
        {["Titular", "Dependente(s)", "Confirmação"].map((label, index) => (
          <Step key={label}>
            <StepLabel
              sx={{
                flexDirection: 'row',
                '& .MuiStepLabel-iconContainer': {
                  mr: 1,
                },
                '& .MuiStepLabel-label': {
                  fontWeight: currentStep === index ? '500' : 'normal',
                },
              }}
            >
              {label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <MissingDataContext.Provider value={{ data, config, handleNextStep, handlePrevStep, handleUpdateData}}>
          {currentStep === 0 && <StepEmployee />}
          {currentStep === 1 && <StepDependents />}
          {currentStep === 2 && <StepConfirmation />}
          {currentStep === 3 && <StepSuccess />}
        </MissingDataContext.Provider>
      </LocalizationProvider>
    </>
  );
}
